import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Keyboard as KeyboardIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  FileCopy as FileCopyIcon,
  ContentCut as ContentCutIcon,
  ContentPaste as ContentPasteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DriveFileRenameOutline as DriveFileRenameIcon,
  CallSplit as CallSplitIcon,
  CallMerge as CallMergeIcon,
  JoinFull as JoinFullIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  FindInPage as FindInPageIcon,
  EditNote as EditNoteIcon,
  Group as GroupIcon,
  FileCopy as FileCopyIcon2,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  helpContent,
  HelpContentKey,
  formatHelpAnswer,
  formatHelpChatAnswer,
  formatHelpGuide,
  resolveHelpContentKey,
} from '../../constants/helpContent';
import api from '../../services/api';
import HelpScreenshot, { HelpScreenshot as HelpScreenshotType } from './HelpScreenshot';
import { HelpScreenshotService } from '../../services/HelpScreenshotService';
interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

interface HelpMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  helpKey?: HelpContentKey;
}

function stepDetailAfterDash(step: string): string {
  const emDash = step.indexOf('—');
  if (emDash >= 0) {
    return step.slice(emDash + 1).trim();
  }
  const arrow = step.indexOf('→');
  if (arrow >= 0) {
    return step.slice(arrow + 1).trim();
  }
  return step;
}

/** Sidebar topics under Help → Shortcuts & gestures (normalized keys). */
function getShortcutsSubtopicAnswer(trimmedLower: string): string | null {
  switch (trimmedLower) {
    case 'keyboard shortcuts':
      return (
        'Keyboard shortcuts (⌘ = Mac Cmd; Ctrl on Windows/Linux)\n\n' +
        'These apply when focus is outside text fields, multiline boxes, and the rich-text editor:\n\n' +
        '• ⌘/Ctrl+Z: Undo\n' +
        '• ⌘/Ctrl+Shift+Z: Redo\n' +
        '• ⌘/Ctrl+X: Cut\n' +
        '• ⌘/Ctrl+C: Copy\n' +
        '• ⌘/Ctrl+V: Paste\n' +
        '• Delete: Delete selected category or content (opens confirmation when allowed). On Mac keyboards that send Backspace for the key labeled Delete, Backspace works the same here when focus is outside text fields.\n' +
        '• ↑ / ↓: Move the selection up or down among siblings when focus is outside the category structure (same as toolbar move arrows).\n' +
        '• ⌘/Ctrl+↑ / ⌘/Ctrl+↓: Same move, works even when the category structure has keyboard focus (works while the category structure has focus, outside text fields and the editor).\n' +
        '• ← / →: When focus is outside the category structure and text fields: with 2+ content rows selected, preview the previous/next selected row; otherwise ← goes back to the parent category in the pane, and → opens the single selected child category row.'
      );
    case 'editing shortcuts':
      return (
        'Editing shortcuts\n\n' +
        'Category structure: inline rename field:\n' +
        '• Enter: Confirm the new name\n' +
        '• Escape: Cancel and revert\n\n' +
        'Ask AI: question field:\n' +
        '• Enter: Send your question\n' +
        '• Shift+Enter: New line in the multiline field\n\n' +
        'Rich-text content editor:\n' +
        '• Standard shortcuts work inside the editor (for example ⌘/Ctrl+Z for undo). Document-wide cut/copy/paste and undo apply when focus is outside the editor.'
      );
    case 'find shortcuts':
      return (
        'Find shortcuts (⌘ = Mac Cmd; Ctrl on Windows/Linux)\n\n' +
        'Open Find:\n' +
        '• ⌘/Ctrl+F: Opens the Find dialog when focus is outside text fields and multiline boxes.\n\n' +
        'While Find is open (outside Find’s search fields):\n' +
        '• ⌘/Ctrl+F: Focus the Find search field and select its text\n' +
        '• Enter: Run / repeat search\n' +
        '• F3 or ⌘/Ctrl+G, Find next match\n' +
        '• Shift+F3 or ⌘/Ctrl+Shift+G, Find previous match\n' +
        '• Ctrl+Shift+F3 or ⌘/Ctrl+Shift+Alt+G, Jump to next category with matches\n' +
        '• Ctrl+Alt+F3 or ⌘/Ctrl+Alt+G, Jump to previous category with matches\n' +
        '• Escape: Close Find'
      );
    case 'navigation':
      return (
        'Navigation\n\n' +
        '• Click document tabs or rows in the document panel to switch documents.\n' +
        '• Click categories in the category structure or rows in the content table to select them.\n' +
        '• Double-click a child category row in the content pane to open it (same as → when one row is selected).\n' +
        '• ← returns to the parent category shown in the pane.\n' +
        '• Expand or collapse branches with the category structure\'s expand controls (and double-click the category label, see Mouse and gestures).\n' +
        '• Tab / Shift+Tab moves focus between controls like in most web apps.\n' +
        '• Use Find (⌘/Ctrl+F) or the search toolbar control to jump to matching categories and content.'
      );
    case 'mouse and gestures':
      return (
        'Mouse and gestures\n\n' +
        '• Click to select categories or content rows.\n' +
        '• ⌘/Ctrl+click: Add or remove an item from the selection (multi-select).\n' +
        '• Shift+click: Select a range among sibling categories or sibling content rows.\n' +
        '• Double-click a category label in the category structure: expand or collapse that branch when it has children.\n' +
        '• ⌘/Ctrl+double-click a branch label: expand or collapse the entire branch under that category.\n' +
        '• Double-click a child category row in the content pane: open that category (show its children or content rows).\n' +
        '• Select a category, then click its label again after a short pause, Start inline rename.\n' +
        '• Right-click: Context menus on documents, categories, and content rows.\n' +
        '• Drag and drop: Move categories or content where targets allow it; hold ⌥ Alt (Option on Mac) while dropping on the category structure where supported to duplicate instead of move.'
      );
    default:
      return null;
  }
}

function answerDescribesAddingCategories(answer: string): boolean {
  const a = answer.toLowerCase();
  return (
    a.includes('add category') ||
    a.includes('adding categories') ||
    a.includes('to add a category:') ||
    a.includes('to add subcategor') ||
    (a.includes("click the '+' button") &&
      (a.includes('parent category') || a.includes('add subcategor')))
  );
}

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [useSemanticSearch, setUseSemanticSearch] = useState(true);
  const [showScreenshots, setShowScreenshots] = useState(true);
  const [currentScreenshots, setCurrentScreenshots] = useState<HelpScreenshotType[]>([]);
  const [unavailableScreenshotIds, setUnavailableScreenshotIds] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <HelpIcon />,
      topics: [
        'How to create a new document',
        'How to add categories',
        'How to add content',
        'Basic navigation',
      ]
    },
    {
      id: 'documents',
      title: 'Document Management',
      icon: <FileCopyIcon />,
      topics: [
        'Creating documents',
        'New document',
        'Opening documents',
        'Closing documents',
        'Deleting documents',
        'Renaming documents',
        'Duplicating documents',
        'Import .nbr archive',
        'Share with user',
        'Merging documents',
        'Compare documents',
      ]
    },
    {
      id: 'categories',
      title: 'Working with Categories',
      icon: <CategoryIcon />,
      topics: [
        'Adding categories',
        'Content category to branch',
        'Deleting categories',
        'Splitting categories',
        'Grouping categories',
        'Flattening categories',
        'Collecting branch content',
        'Merging categories',
        'Renaming categories',
        'Moving categories',
        'Drag and drop categories',
        'Cutting, copying, and pasting categories',
        'Import branch from document',
        'Duplicate as document',
        'Category Structure Overview',
        'Category organization',
      ]
    },
    {
      id: 'content',
      title: 'Working with Content',
      icon: <DescriptionIcon />,
      topics: [
        'Adding content',
        'Editing content',
        'Deleting content',
        'Moving content',
        'Drag and drop content',
        'Cutting, copying, and pasting content',
        'Select subcase',
        'Rename subcase',
        'Manage subcase groups',
        'Filter content by subcase',
        'Compare content and select',
        'Content organization',
      ]
    },
    {
      id: 'search',
      title: 'Search and Find',
      icon: <SearchIcon />,
      topics: [
        'Finding text in documents',
        'Find: semantic mode',
        'Search options',
        'Navigation between matches',
      ]
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: <AutoAwesomeIcon />,
      topics: [
        'Structure builder',
        'Structure builder: Instructions (AI)',
        'Structure builder: Import tab',
        'Ask AI',
        'Structure categories (AI)',
        'Add matching content (AI)',
        'New document from files (AI)',
        'Add to document from files (AI)',
        'Tips for AI features',
      ]
    },
    {
      id: 'shortcuts',
      title: 'Shortcuts & gestures',
      icon: <KeyboardIcon />,
      topics: [
        'Keyboard shortcuts',
        'Editing shortcuts',
        'Find shortcuts',
        'Navigation',
        'Mouse and gestures',
      ]
    },
    {
      id: 'settings',
      title: 'Settings and Preferences',
      icon: <SettingsIcon />,
      topics: [
        'Theme: light, dark, or system',
        'Toolbar: icon size and dock magnification',
        'Auto-paste settings',
        'Tooltip settings',
        'Help mode and short tooltips',
        'User preferences',
      ]
    }
  ];

  const quickQuestions = [
    'How do I create a new document?',
    'How do I load a document from a file?',
    'How do I add a category?',
    'How can I convert a content category to a branch?',
    'How do I delete a category?',
    'How do I add content to a category?',
    'How do I filter content by subcase?',
    'How do I search in my document?',
    'How do I delete a document?',
    'How do I rename a document?',
    'How do I use the structure builder?',
    'What is the structure builder Instructions tab?',
    'What is the structure builder Import tab?',
    'How do I use Ask AI?',
    'How do I add to document from files (AI)?',
    'How do I add matching content (AI)?',
    'How do I compare content and select?',
    'How do I create a new document from files (AI)?',
    'How do I duplicate a category as a document?',
    'How do I use Category Structure Overview?',
    'How do I rename a subcase?',
    'How do I manage subcase groups?',
    'How do I share a document with another user?',
    'How do I import a branch from another document?',
    'How do I merge categories?',
    'How do I change toolbar icon size or dock magnification?',
    'How do I export my document?',
  ];

  useEffect(() => {
    if (open) {
      // Add welcome message
      setMessages([{
        id: 'welcome',
        type: 'assistant',
        content: 'Hello! I\'m here to help you learn how to use Narralyze. You can ask me questions about any feature (including Structure builder, Ask AI, toolbar settings, and themes), or browse the help topics below. What would you like to know?',
        timestamp: new Date()
      }]);
    }
  }, [open]);

  useEffect(() => {
    setUnavailableScreenshotIds(new Set());
  }, [currentScreenshots]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getCategoryAnswer = (categoryId: string): string | null => {
    switch (categoryId) {
      case 'getting-started':
        return (
          'Getting Started with Narralyze:\n\n' +
          '1. Create a document, Document Menu → New.. in the document panel\n' +
          '2. Build structure, new documents start with a root content category; add rows there, or use Split category or Structure builder to create folders; Add category (+) is available once a branch has at least two subcategories\n' +
          '3. Add content, select a content category and click Add Content\n' +
          '4. Subcases, label rows within a category (dropdown when editing)\n' +
          '5. Find: ⌘/Ctrl+F to search names, descriptions, and content\n' +
          '6. Ask AI: sparkles icon for document Q&A when enabled\n' +
          '7. Structure builder, handyman icon to build or change the category structure\n' +
          '8. Toolbar focus: moving the pointer into the top bar focuses the first control when focus is outside fields and the editor'
        );
      
      case 'categories':
        return (
          'Working with Categories:\n\n' +
          'Adding categories:\n' +
          '• Select a branch folder with at least two subcategories and click + in the toolbar\n' +
          '• Enter a name and description, then Add\n' +
          '• The new category is a content category under that branch (use Split category later for subfolders)\n\n' +
          'Add is only available under a branch with at least two subcategories.\n\n' +
          'Managing categories (toolbar):\n' +
          '• Delete: toolbar, context menu, or Delete/Backspace outside text fields\n' +
          '• Split category, divide a content category into two children; rows go to a temp category, or split by selection into first/second\n' +
          '• Group categories, selected siblings under a new parent folder\n' +
          '• Flatten category, promote children up one level\n' +
          '• Collect branch content, gather all rows into one content category\n' +
          '• Merge categories, combine selected content categories into one\n' +
          '• Structure categories (AI), cluster rows or add parent layers\n' +
          '• Rename, change name and description in the dialog; toolbar, context menu, or inline name edit (Settings)\n' +
          '• Move, up/down arrows reorder siblings under the same parent (category structure: one category; content pane: consecutive multi-select)\n' +
          '• Drag and drop, reorder before/after a sibling or drop inside a branch to reparent; hold ⌥ Alt to duplicate where supported\n' +
          '• Cut, copy, paste, cut stays in one document; copy works across open tabs\n' +
          '• Import branch from document, clone under a branch with at least two subcategories\n' +
          '• Duplicate as document, new library document from a branch\n' +
          '• Category Structure Overview, every category in scope with its content at once'
        );
      
      case 'content':
        return (
          'Working with Content:\n\n' +
          'Adding content:\n' +
          '• Select a content category (a leaf category that holds text rows)\n' +
          '• Click Add Content in the toolbar\n' +
          '• Enter text and choose or create a subcase, then Save\n\n' +
          'Editing content:\n' +
          '• Select a content row and click Edit Content\n' +
          '• Modify text and subcase; changes save automatically\n\n' +
          'Organizing content:\n' +
          '• Subcases label rows within a category\n' +
          '• Rename subcase, change a label everywhere in the document\n' +
          '• Manage subcase groups, group subcases so you can select them together in filters and dialogs\n' +
          '• Filter content by subcase, category structure stays visible; only matching rows are shown\n' +
          '• Compare content and select, match rows against another category or branch (feature flag)\n\n' +
          'Moving content:\n' +
          '• Move arrows reorder rows within the same content category\n' +
          '• Drag and drop reorders within a category or moves rows to another content category in the category structure\n' +
          '• Cut, copy, and paste move or duplicate rows between content categories'
        );
      
      case 'documents':
        return (
          'Document Management:\n\n' +
          'Document panel layout:\n' +
          '• Document Menu, main actions for the selected document\n' +
          '• Shared with me / Shared by me, pending copy invitations (when sharing is enabled)\n' +
          '• View only shared with me / View only shared by me, live read-only grants\n' +
          '• Open Documents, tabs currently open; Closed Documents, saved in your library\n' +
          '• Single-click a row to select; double-click a closed document (or click its icon) to open\n\n' +
          'Creating documents:\n' +
          '• New.. — empty document (Document Menu)\n' +
          '• Import .nbr archive.. — legacy desktop backup\n' +
          '• New document from files (AI).. — upload files; AI proposes structure\n' +
          '• Duplicate.. — copy a document in your library\n' +
          '• Duplicate as document — copy a category branch to a new document\n' +
          '• Structure builder — create via Instructions (AI) or Import tab (no document tab)\n' +
          '• Merge.. → merge into a new document\n\n' +
          'Opening / closing:\n' +
          '• Open: double-click, document icon, Document Menu → Open, or right-click → Open\n' +
          '• Close: Document Menu → Close, document icon on an open row, or the editor tab ×\n' +
          '• Closing moves the document to Closed Documents; edits save automatically while open\n\n' +
          'Import:\n' +
          '• Import .nbr archive.., legacy desktop backup file; opens in a new tab\n\n' +
          'Managing documents:\n' +
          '• Rename.., Delete, Duplicate.., Document Menu or right-click (your documents only)\n' +
          '• Share with user.., copy to library or live view only (when sharing is enabled)\n' +
          '• Merge.., combine two documents; Compare.., side-by-side review in a dialog when enabled\n' +
          '• Add to document from files (AI).., extend the open document from uploaded files'
        );
      
      case 'search':
        return "Search and Find Features:\n\nText Search:\n• Click the search icon in the toolbar, or press ⌘/Ctrl+F when focus is outside text fields and multiline boxes\n• Enter your search terms\n• Choose search options (case sensitive, whole word, etc.)\n• Use 'Find Next' or F3 / ⌘/Ctrl+G to navigate through matches (when Find has focus outside search fields)\n\nSearch Options:\n• Search in: Choose to search names, descriptions, or content\n• Search mode: Any (at least one term), All (all terms), or Exact phrase\n• Case sensitive: Match exact letter case\n• Whole word: Match complete words only\n\nSemantic (Find):\n• In the Find dialog, choose Semantic mode to search by meaning and jump to matching rows\n• Ask AI (sparkles icon) opens a chat panel for document Q&A";
      
      case 'ai-features':
        return (
          'AI Features in Narralyze:\n\n' +
          'Structure builder (toolbar handyman icon when enabled):\n' +
          '• Instructions (AI): natural-language category structure operations; review proposal, then Apply (undo/redo).\n' +
          '• Import: paste folder outlines and/or Subcase: content blocks in one editor; Load file (.txt/.md); Build proposal from paste.\n' +
          '• With no document tab you can create or duplicate a document via Instructions (AI). With a tab open the model sees your category structure.\n' +
          '• Voice (optional): dictate into the field; Voice dropdown sets speech locale (saved in the browser).\n\n' +
          'Ask AI (sparkles icon):\n' +
          '• Chat panel: ask questions about the open document; AI answers using semantic retrieval over your content. Multi-turn conversation.\n\n' +
          'Structure categories (AI):\n' +
          '• Content rows: open Structure categories (AI); choose top-down or bottom-up (bottom-up is the default), depth, preview, apply. Many subcategories: same command adds parent layers.\n\n' +
          'Generate report (AI):\n' +
          '• Category context menu: qualitative narrative in the document’s main language, with many quoted excerpts; copy, print, or Export DOCX from the reader.\n\n' +
          'Add matching content (AI):\n' +
          '• With a content category selected: copy or move semantically matching rows from another content category on the path to the document root (grouped by folder). Apply all, step-by-step, or quick review. Same similarity threshold as Find (Semantic). Rows already in the target (same subcase and text) are skipped.\n\n' +
          'From files in the document panel (when enabled):\n' +
          '• New document from files (AI), upload PDF/Word/etc. to create a new document structure.\n' +
          '• Add to document from files (AI), add matching categories and rows to the open document.\n\n' +
          'Tips: Use Structure builder for structural edits; Add matching content (AI) to pull rows between categories; Ask AI for Q&A and analysis; Find (⌘/Ctrl+F) to locate text (including Semantic mode).'
        );
      
      case 'shortcuts':
        return (
          'Shortcuts & gestures, pick a topic on the left:\n\n' +
          '• Keyboard shortcuts, Undo/redo, cut, copy, paste, Delete/Backspace, move (↑/↓ or ⌘/Ctrl+↑↓) (outside text fields)\n' +
          '• Editing shortcuts, Inline rename keys, Ask AI question field, editor note\n' +
          '• Find shortcuts, Open Find, matches, jump between categories\n' +
          '• Navigation, Tabs, category structure focus, Tab key\n' +
          '• Mouse and gestures, Multi-select, drag-and-drop, context menus'
        );
      
      case 'settings':
        return (
          'Settings and Preferences (gear icon on the top bar):\n\n' +
          'Appearance:\n' +
          '• Theme: System, Light, or Dark, dark mode uses readable panels in Help and for detailed help tooltips.\n' +
          '• Toolbar icon size, slider for main bar icon scale.\n' +
          '• Dock-style toolbar icons, optional macOS Dock–style magnification as you move the pointer along the bar.\n' +
          '• Toolbar keyboard focus, moving the pointer into the bar from outside focuses the first control when you are not typing in an input, textarea, select, or the rich-text editor (always on; no setting).\n\n' +
          'Behavior:\n' +
          '• Auto-paste from clipboard when adding content (Chrome/Edge: one-time allow; Safari: may need Paste or ⌘V).\n' +
          '• Detailed help tooltips, longer explanations on controls.\n' +
          '• Short tooltips, brief hints on menu rows (can pair with help mode).\n' +
          '• Tooltip font size, slider stored in the browser.\n\n' +
          'User menu: avatar and profile options. Most toggles save automatically to local storage.'
        );
      
      default:
        return null;
    }
  };

  const handleSendQuestion = async () => {
    if (!question.trim()) return;

    const trimmedQuestion = question.trim();
    const userMessage: HelpMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: trimmedQuestion,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    try {
      // If user typed an exact sidebar/quick topic, prefer the deterministic fallback answer
      // (avoids surprising backend answers for canned topics).
      const isKnownTopic = helpCategories.some((c) => c.topics.includes(trimmedQuestion)) || quickQuestions.includes(trimmedQuestion);
      if (isKnownTopic) {
        const helpKey = resolveHelpContentKey(trimmedQuestion);
        const answer = formatHelpAnswer(trimmedQuestion, getFallbackAnswer(trimmedQuestion));
        const screenshots = resolveScreenshotsForQuestion(trimmedQuestion, answer);
        const assistantMessage: HelpMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: answer,
          helpKey: helpKey ?? undefined,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        setCurrentScreenshots(screenshots);
        setUnavailableScreenshotIds(new Set());
        setIsLoading(false);
        return;
      }

      const endpoint = useSemanticSearch ? '/help/semantic-search' : '/help/ask';
      const response = await api.post(endpoint, {
        question: trimmedQuestion,
        context: selectedCategory ? selectedCategory : (useSemanticSearch ? 'semantic' : 'pattern')
      });

      // If we have a selected category and the API response seems generic,
      // try to get a more specific answer from our fallback system
      let answer = response.data.answer;
      if (selectedCategory && (answer.includes('Adding Categories') || answer.includes('Managing Categories'))) {
        // The API returned generic category content, try to get a specific answer
        const specificAnswer = getFallbackAnswer(trimmedQuestion);
        if (specificAnswer && !specificAnswer.includes('I understand you\'re asking about')) {
          answer = specificAnswer;
        }
      }

      // Get screenshots for this topic when available
      // IMPORTANT: use the original user question, not the cleared state variable.
      let screenshots = resolveScreenshotsForQuestion(trimmedQuestion, answer);
      const helpKey = resolveHelpContentKey(trimmedQuestion);
      answer = formatHelpAnswer(trimmedQuestion, answer);

      const assistantMessage: HelpMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: answer,
        helpKey: helpKey ?? undefined,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      setCurrentScreenshots(screenshots);
      setUnavailableScreenshotIds(new Set());
    } catch (error) {
      console.error('Error asking help question:', error);
      const helpKey = resolveHelpContentKey(trimmedQuestion);
      const fallbackAnswer = formatHelpAnswer(trimmedQuestion, getFallbackAnswer(question));
      const screenshots = resolveScreenshotsForQuestion(trimmedQuestion, fallbackAnswer);
      const errorMessage: HelpMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: fallbackAnswer,
        helpKey: helpKey ?? undefined,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      setCurrentScreenshots(screenshots);
      setUnavailableScreenshotIds(new Set());
    } finally {
      setIsLoading(false);
    }
  };

  const getFallbackAnswer = (question: string): string => {
    const trimmedLower = question.trim().toLowerCase();
    const shortcutsTopicAnswer = getShortcutsSubtopicAnswer(trimmedLower);
    if (shortcutsTopicAnswer) {
      return shortcutsTopicAnswer;
    }

    if (trimmedLower.includes('join') && trimmedLower.includes('categor')) {
      return (
        'Group categories:\n\n' +
        formatHelpGuide('group-categories')
      );
    }

    const resolvedKey = resolveHelpContentKey(question);
    if (resolvedKey) {
      return formatHelpGuide(resolvedKey);
    }

    const lowerQuestion = trimmedLower;

    if (
      lowerQuestion.includes('duplicate') &&
      (lowerQuestion.includes('category') || lowerQuestion.includes('categories')) &&
      !lowerQuestion.includes('document')
    ) {
      return formatHelpGuide('cut-copy-paste-categories');
    }

    
    if (lowerQuestion.includes('content organization') || question === 'Content organization') {
      return "Content Organization Tips:\n\nUsing Subcases:\n• Create subcases to organize content within categories\n• Use subcase groups for better organization\n• Assign content to specific subcases for better structure\n\nMoving Content:\n• Move arrows reorder rows within the same content category\n• Drag and drop reorders within a category or moves rows to another content category\n• Cut, copy, and paste move or duplicate rows between content categories\n\nContent Categories:\n• Branch folders hold subcategories; content categories hold text rows\n• Use content categories for storing text\n• Use branch folders for organization";
    }
    
    if (lowerQuestion.includes('category organization') || question === 'Category organization') {
      return "Category Organization Tips:\n\nHierarchical Structure:\n• Create a logical hierarchy with main categories and subcategories\n• Use descriptive names that clearly indicate the category's purpose\n• Keep the structure consistent throughout your document\n\nOrganizing Principles:\n• Group related items together under common parent categories\n• Use categories for organization, content categories for actual text\n• Maintain a balance between depth and breadth in your structure\n\nBest Practices:\n• Start with broad categories and add specific subcategories as needed\n• Use consistent naming conventions across your document\n• Regularly review and reorganize categories as your content grows\n• Use Structure builder for large structural changes; use Ask AI to explore and analyze document content";
    }
    
    if (
      trimmedLower === 'help mode and short tooltips' ||
      lowerQuestion.includes('help mode') ||
      lowerQuestion === 'help mode' ||
      question === 'Help mode'
    ) {
      return (
        'Help mode and tooltips:\n\n' +
        `${helpContent['help-mode'].simple}, ${helpContent['help-mode'].detailed}\n\n` +
        `${helpContent['short-tooltips'].simple}, ${helpContent['short-tooltips'].detailed}\n\n` +
        `${helpContent['tooltip-font-size'].simple}, ${helpContent['tooltip-font-size'].detailed}\n\n` +
        'Open Help from the ? icon on the top bar to browse topics or ask in chat. In dark theme, detailed help tooltips use a high-contrast panel so text stays readable.'
      );
    }
    
    if (trimmedLower === 'tips for ai features') {
      return getCategoryAnswer('ai-features') ?? formatHelpGuide('ask-ai');
    }

    // Navigation (generic, not "Navigation between matches", which resolves above)
    if (
      (lowerQuestion.includes('navigate') || lowerQuestion.includes('navigation')) &&
      !lowerQuestion.includes('between matches')
    ) {
      return "Navigation in Narralyze:\n• Click on categories to select them\n• Use the category structure to expand/collapse categories\n• Use tabs to switch between documents\n• Use the search feature to find specific content\n• Use keyboard shortcuts for faster navigation";
    }

    if (
      trimmedLower === 'how do i change toolbar icon size or dock magnification?' ||
      question.includes('Toolbar:') ||
      (lowerQuestion.includes('toolbar') &&
        (lowerQuestion.includes('icon size') ||
          lowerQuestion.includes('dock') ||
          lowerQuestion.includes('magnif')))
    ) {
      const hi = helpContent['toolbar-icon-size'];
      const dock = helpContent['toolbar-dock-magnify'];
      return (
        `${hi.simple}\n${hi.detailed}\n\n${dock.simple}\n${dock.detailed}\n\n` +
        'Keyboard focus: when you move the pointer into the top bar from outside, the first enabled toolbar button is focused automatically unless focus is in a text field or the editor (always on; not configurable from Settings).'
      );
    }

    if (question.includes('Theme:') || trimmedLower === 'theme, light, dark, or system') {
      return (
        `${helpContent['settings-theme-system'].simple}\n${helpContent['settings-theme-system'].detailed}\n\n` +
        `${helpContent['settings-theme-light'].simple}\n${helpContent['settings-theme-light'].detailed}\n\n` +
        `${helpContent['settings-theme-dark'].simple}\n${helpContent['settings-theme-dark'].detailed}`
      );
    }

    // Settings
    if (lowerQuestion.includes('settings') || lowerQuestion.includes('preferences')) {
      return (
        `${helpContent['settings'].simple}\n${helpContent['settings'].detailed}\n\n` +
        'Open the gear icon on the top bar. Application font size is adjusted from the same menu. Changes are stored in the browser (local storage) unless noted otherwise.'
      );
    }
    
    
    // Shortcuts & gestures, vague chat queries (exact sidebar titles handled by getShortcutsSubtopicAnswer)
    if (
      lowerQuestion.includes('shortcut') ||
      lowerQuestion.includes('keyboard') ||
      (lowerQuestion.includes('mouse') && lowerQuestion.includes('gesture'))
    ) {
      return (
        'Shortcuts & gestures are grouped by topic in the left sidebar under Shortcuts & gestures:\n\n' +
        '• Keyboard shortcuts, Undo/redo, cut, copy, paste, Delete/Backspace, move (↑/↓ or ⌘/Ctrl+↑↓) (outside text fields)\n' +
        '• Editing shortcuts, Inline rename, semantic AI field, rich-text editor note\n' +
        '• Find shortcuts, Open Find, match navigation, jump between categories\n' +
        '• Navigation, Documents, category structure, Tab, search\n' +
        '• Mouse and gestures, Multi-select, double-click, rename click, drag-and-drop\n\n' +
        'Open one of those topics for the full key list.'
      );
    }
    
    
    // File upload for AI (narrow, avoid matching generic "file" / .nbr import)
    if (
      (lowerQuestion.includes('upload') && (lowerQuestion.includes('ai') || lowerQuestion.includes('pdf') || lowerQuestion.includes('word'))) ||
      (lowerQuestion.includes('file') &&
        (lowerQuestion.includes('ai') ||
          lowerQuestion.includes('suggest') ||
          lowerQuestion.includes('new document from files') ||
          lowerQuestion.includes('add to document from files')))
    ) {
      return (
        'Upload files for AI (document panel)\n\n' +
        '• New document from files (AI), creates a new document from uploaded files.\n' +
        '• Add to document from files (AI), extends the open document (select a document tab first).\n\n' +
        'Select PDF, Word, or other text files, provide context, set depth and strategy, then review before applying.'
      );
    }
    
    // Import files for AI (not .nbr archive or branch import)
    if (
      lowerQuestion.includes('import') &&
      (lowerQuestion.includes('file') || lowerQuestion.includes('pdf') || lowerQuestion.includes('word')) &&
      !lowerQuestion.includes('nbr') &&
      !lowerQuestion.includes('branch') &&
      !lowerQuestion.includes('branch')
    ) {
      return (
        'Import files with AI (document panel)\n\n' +
        'Use New document from files (AI) to create a new document, or Add to document from files (AI) to extend the open document. ' +
        'For a Narralyzer backup, use Import .nbr archive in the Document Menu instead.'
      );
    }
    
    if (lowerQuestion.includes('subcase group')) {
      return formatHelpGuide('manage-subcase-groups');
    }

    if (
      lowerQuestion.includes('select subcase') ||
      (lowerQuestion.includes('select') && lowerQuestion.includes('subcase') && !lowerQuestion.includes('filter') && !lowerQuestion.includes('group'))
    ) {
      return formatHelpGuide('select-subcase');
    }
    
    if (lowerQuestion.includes('subcase')) {
      return (
        'Subcases label rows within a content category:\n' +
        '1. When adding or editing content, choose a subcase from the dropdown or type a new name\n' +
        '2. Use Manage subcase groups to organize labels for filters\n' +
        '3. Use Filter content by subcase to show only certain labels in the content pane'
      );
    }
    
    // Basic operations
    if (lowerQuestion.includes('basic') || lowerQuestion.includes('getting started')) {
      return getCategoryAnswer('getting-started') ?? formatHelpGuide('new-document');
    }
    
    // Default answer
    return "I understand you're asking about \"" + question + "\". Here are some general tips:\n\n" +
           "• Use the toolbar buttons to perform actions on selected categories or content\n" +
           "• Right-click on items for context menus with additional options\n" +
           "• Use the search feature to find specific content\n" +
           "• Ask AI (sparkles) for document Q&A; Structure builder (handyman) for building or changing the category structure when enabled\n" +
           "• Check the help topics in the left sidebar for detailed guides\n\n" +
           "Could you be more specific about what you'd like to do? I can provide more targeted help.";
  };

  const handleQuickQuestion = (q: string) => {
    setQuestion(q);
    showHelpForQuestion(q);
  };

  const showHelpForKey = (key: HelpContentKey) => {
    const entry = helpContent[key];
    const label = entry?.simple ?? key;
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        type: 'user',
        content: label,
        timestamp: new Date(),
      },
    ]);
    setIsLoading(true);
    const answer = formatHelpChatAnswer(key);
    const screenshots = HelpScreenshotService.getScreenshotsForHelpKey(key);
    setMessages((prev) => [
      ...prev,
      {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: answer,
        helpKey: key,
        timestamp: new Date(),
      },
    ]);
    setCurrentScreenshots(screenshots);
    setUnavailableScreenshotIds(new Set());
    setIsLoading(false);
  };

  const showHelpForQuestion = (q: string) => {
    const userMessage: HelpMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: q,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const helpKey = resolveHelpContentKey(q);
    const answer = formatHelpAnswer(q, getFallbackAnswer(q));
    const screenshots = resolveScreenshotsForQuestion(q, answer);
    const assistantMessage: HelpMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: answer,
      helpKey: helpKey ?? undefined,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMessage]);
    setCurrentScreenshots(screenshots);
    setUnavailableScreenshotIds(new Set());
    setIsLoading(false);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const getHelpContent = (key: HelpContentKey) => {
    return helpContent[key] || { simple: key, detailed: 'Help content not found.' };
  };

  const resolveScreenshotsForQuestion = (question: string, answer?: string) => {
    const fromTopic = HelpScreenshotService.getScreenshotsForQuestion(question);
    if (fromTopic.length > 0) {
      return fromTopic;
    }
    const helpKey = resolveHelpContentKey(question);
    if (helpKey) {
      const fromKey = HelpScreenshotService.getScreenshotsForHelpKey(helpKey);
      if (fromKey.length > 0) {
        return fromKey;
      }
      // Resolved help topic with no visual assets — do not infer from answer text
      // (e.g. structure builder mentions "document tab" but is not Open document).
      return [];
    }
    if (answer) {
      return getScreenshotsForAnswer(answer);
    }
    return [];
  };

  const getScreenshotsForAnswer = (answer: string) => {
    const lowerAnswer = answer.toLowerCase();

    if (lowerAnswer.includes('structure builder') || lowerAnswer.includes('tree assistant')) {
      return [];
    }

    if (
      lowerAnswer.includes('ways to create a document') ||
      lowerAnswer.includes('several paths add a document')
    ) {
      return [];
    }

    if (answerDescribesAddingCategories(answer)) {
      return HelpScreenshotService.getScreenshotsForTopic('add category');
    }
    
    // Check if the answer contains import .nbr archive related content
    if (
      (lowerAnswer.includes('import') && lowerAnswer.includes('nbr')) ||
      (lowerAnswer.includes('load') && (lowerAnswer.includes('document') || lowerAnswer.includes('file')))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('import nbr archive');
    }
    
    // Check if the answer contains create document related content
    if (
      lowerAnswer.includes('create a new document') ||
      lowerAnswer.includes('create document') ||
      lowerAnswer.includes('new document')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('create document');
    }

    // Check if the answer contains open document related content
    if (lowerAnswer.includes('open') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('open document');
    }

    // Check if the answer contains close document related content
    if (lowerAnswer.includes('close') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('close document');
    }

    // Check if the answer contains delete document related content
    if (lowerAnswer.includes('delete') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('delete document');
    }

    // Check if the answer contains rename document related content
    if (lowerAnswer.includes('rename') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('rename document');
    }
    
    // Check if the answer contains duplicate document related content
    if (lowerAnswer.includes('duplicate') && lowerAnswer.includes('as') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('duplicate as document');
    }
    if (lowerAnswer.includes('duplicate') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('duplicate document');
    }

    // Check if the answer contains new document from files (AI) related content
    if (
      (lowerAnswer.includes('new document') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('suggest') && lowerAnswer.includes('tree')) ||
      (lowerAnswer.includes('suggest') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('category') && lowerAnswer.includes('tree') && lowerAnswer.includes('files'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('new document from files');
    }

    // Check if the answer contains add to document from files (AI) related content
    if (
      (lowerAnswer.includes('add to document') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('expand') && lowerAnswer.includes('document')) ||
      (lowerAnswer.includes('expand') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('expand') && lowerAnswer.includes('with files'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('add to document from files');
    }

    // Check if the answer contains merge document related content
    if (lowerAnswer.includes('merge') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('merge document');
    }

    // Check if the answer contains split category related content (specific guide, not category overview)
    if (
      lowerAnswer.includes('to split a category:') ||
      lowerAnswer.startsWith('to split a category')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('split category');
    }

    // Check if the answer contains add matching content (AI) related content
    if (
      lowerAnswer.includes('add matching content') ||
      (lowerAnswer.includes('matching rows') && lowerAnswer.includes('another category'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('add matching content (ai)');
    }

    if (
      lowerAnswer.includes('compare content and select') ||
      (lowerAnswer.includes('compare') &&
        lowerAnswer.includes('select unmatched') &&
        lowerAnswer.includes('match rule'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('compare content and select');
    }

    // Check if the answer contains edit content related content
    if (
      lowerAnswer.includes('editing content') ||
      lowerAnswer.includes('edit content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('editing content');
    }

    // Check if the answer contains delete content related content
    if (
      lowerAnswer.includes('deleting content') ||
      lowerAnswer.includes('delete content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('deleting content');
    }

    // Drag and drop content (before move-content matching)
    if (
      (lowerAnswer.includes('drag') || lowerAnswer.includes('drop')) &&
      lowerAnswer.includes('content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('drag and drop content');
    }

    // Move content
    if (lowerAnswer.includes('mov') && lowerAnswer.includes('content')) {
      return HelpScreenshotService.getScreenshotsForTopic('moving content');
    }

    // Cut/copy/paste content
    if (
      (lowerAnswer.includes('cut') || lowerAnswer.includes('copy') || lowerAnswer.includes('paste')) &&
      lowerAnswer.includes('content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('cutting, copying, and pasting content');
    }

    // Check if the answer contains add content related content
    if (
      lowerAnswer.includes('add content') ||
      lowerAnswer.includes('adding content') ||
      lowerAnswer.includes('add a content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('add content');
    }

    // Check if the answer contains rename category related content
    if (lowerAnswer.includes('rename') && lowerAnswer.includes('category')) {
      return HelpScreenshotService.getScreenshotsForTopic('rename category');
    }

    // Check if the answer contains delete category related content
    if (lowerAnswer.includes('delete') && lowerAnswer.includes('category')) {
      return HelpScreenshotService.getScreenshotsForTopic('delete category');
    }

    // Check if the answer contains group categories related content
    if (lowerAnswer.includes('group') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('group categories');
    }

    // Drag and drop categories (before move-category matching)
    if (
      (lowerAnswer.includes('drag') || lowerAnswer.includes('drop')) &&
      lowerAnswer.includes('categor')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('drag and drop categories');
    }

    // Check if the answer contains move category related content
    if (lowerAnswer.includes('mov') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('moving categories');
    }

    // Check if the answer contains cut/copy/paste category related content
    if (
      (lowerAnswer.includes('cut') || lowerAnswer.includes('copy') || lowerAnswer.includes('paste')) &&
      lowerAnswer.includes('categor')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('cutting, copying, and pasting categories');
    }

    // Check if the answer contains merge categories related content
    if (lowerAnswer.includes('merge') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('merging categories');
    }

    // Check if the answer contains flatten category related content
    if (lowerAnswer.includes('flatten') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('flattening categories');
    }

    if (lowerAnswer.includes('collect') && (lowerAnswer.includes('branch') || lowerAnswer.includes('branch content'))) {
      return HelpScreenshotService.getScreenshotsForTopic('collect branch content');
    }

    if (
      (lowerAnswer.includes('structure') && lowerAnswer.includes('categor')) ||
      (lowerAnswer.includes('initial') && lowerAnswer.includes('categor')) ||
      (lowerAnswer.includes('to structure categories') || lowerAnswer.includes('to structure categories (ai)')) ||
      (lowerAnswer.includes('parent') && lowerAnswer.includes('layer')) ||
      (lowerAnswer.includes('branch') && lowerAnswer.includes('generat'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('structure categories');
    }

    if (lowerAnswer.includes('import') && lowerAnswer.includes('document') && (lowerAnswer.includes('branch') || lowerAnswer.includes('branch'))) {
      return HelpScreenshotService.getScreenshotsForTopic('import branch from document');
    }

    if (lowerAnswer.includes('share with user') || (lowerAnswer.includes('share copy') && lowerAnswer.includes('document'))) {
      return HelpScreenshotService.getScreenshotsForTopic('share with user');
    }

    if (
      lowerAnswer.includes('select subcase') ||
      (lowerAnswer.includes('content panel header') && lowerAnswer.includes('editing focus'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('select subcase');
    }

    if (lowerAnswer.includes('rename') && lowerAnswer.includes('subcase')) {
      return HelpScreenshotService.getScreenshotsForTopic('rename subcase');
    }

    if (lowerAnswer.includes('manage subcase groups') || (lowerAnswer.includes('subcase group') && lowerAnswer.includes('manage'))) {
      return HelpScreenshotService.getScreenshotsForTopic('manage subcase groups');
    }

    if (lowerAnswer.includes('full-tree view') || lowerAnswer.includes('full tree view') || lowerAnswer.includes('category structure overview') || lowerAnswer.includes('category tree overview') || lowerAnswer.includes('branch view') || (lowerAnswer.includes('show') && (lowerAnswer.includes('sub tree') || lowerAnswer.includes('subtree') || lowerAnswer.includes('branch')))) {
      return HelpScreenshotService.getScreenshotsForTopic('category structure overview');
    }

    if (lowerAnswer.includes('content pane') && lowerAnswer.includes('subcase') && lowerAnswer.includes('filter')) {
      return HelpScreenshotService.getScreenshotsForTopic('filter content by subcase');
    }

    if (lowerAnswer.includes('ask ai') || (lowerAnswer.includes('sparkles') && lowerAnswer.includes('document'))) {
      return HelpScreenshotService.getScreenshotsForTopic('ask ai');
    }

    if (lowerAnswer.includes('show') && (lowerAnswer.includes('sub tree') || lowerAnswer.includes('branch'))) {
      return HelpScreenshotService.getScreenshotsForTopic('show sub tree');
    }
    
    return [];
  };

  const renderStructuredHelpAnswer = (helpKey: HelpContentKey) => {
    const entry = helpContent[helpKey];
    if (!entry) {
      return null;
    }
    const drillDownKeys = entry.stepHelpKeys;
    const hasDrillDown =
      !!entry.steps?.length &&
      !!drillDownKeys?.length &&
      drillDownKeys.length === entry.steps.length;

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.simple}
        </Typography>
        {entry.detailed?.trim() ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.5 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {entry.detailed.trim()}
            </Typography>
          </>
        ) : null}
        {entry.steps?.length ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.5 }}>
              Steps:
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>
              {entry.steps.map((step, index) => {
                const childKey = drillDownKeys?.[index] as HelpContentKey | undefined;
                const childEntry = childKey ? helpContent[childKey] : undefined;
                const detail = stepDetailAfterDash(step);
                return (
                  <Box component="li" key={index} sx={{ mb: 0.75 }}>
                    <Typography variant="body2" component="span">
                      {hasDrillDown && childKey && childEntry ? (
                        <>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => showHelpForKey(childKey)}
                            sx={{ verticalAlign: 'baseline', fontWeight: 600 }}
                          >
                            {childEntry.simple}
                          </Link>
                          {detail ? ` — ${detail}` : null}
                        </>
                      ) : (
                        step
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : null}
      </Box>
    );
  };

  const renderMessage = (message: HelpMessage) => (
    <Box
      key={message.id}
      sx={{
        display: 'flex',
        justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        sx={(theme) => ({
          p: 2,
          maxWidth: '80%',
          backgroundColor:
            message.type === 'user'
              ? theme.palette.primary.main
              : theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[100],
          color:
            message.type === 'user'
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
        })}
      >
        {message.type === 'assistant' && message.helpKey ? (
          renderStructuredHelpAnswer(message.helpKey) ?? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {message.content}
            </Typography>
          )
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {message.content}
          </Typography>
        )}
      </Paper>
    </Box>
  );

  const visibleScreenshots = currentScreenshots.filter(
    (screenshot) => !unavailableScreenshotIds.has(screenshot.id),
  );

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon />
          <Typography variant="h6">Help & Support</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left sidebar with help categories */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', p: 2, overflowY: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Help Topics</Typography>
            
            {/* Quick Questions */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              Quick Questions
            </Typography>
            <Box sx={{ mb: 3 }}>
              {quickQuestions.map((q, index) => (
                <Chip
                  key={index}
                  label={q}
                  size="small"
                  onClick={() => handleQuickQuestion(q)}
                  sx={{ m: 0.5, cursor: 'pointer' }}
                  variant="outlined"
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Help Categories */}
            {helpCategories.map((category) => (
              <Accordion
                key={category.id}
                expanded={selectedCategory === category.id}
                onChange={() => handleCategorySelect(selectedCategory === category.id ? '' : category.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    <Typography variant="subtitle2">{category.title}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {category.topics.map((topic, index) => (
                      <ListItem
                        key={index}
                        button
                        onClick={() => handleQuickQuestion(topic)}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemText
                          primary={topic}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Right side with chat */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Messages area */}
            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
              {messages.map(renderMessage)}
              
              {showScreenshots && visibleScreenshots.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Visual guide
                  </Typography>
                  {visibleScreenshots.map((screenshot) => (
                    <HelpScreenshot
                      key={screenshot.id}
                      screenshot={screenshot}
                      onImageError={() =>
                        setUnavailableScreenshotIds((prev) => new Set(prev).add(screenshot.id))
                      }
                    />
                  ))}
                </Box>
              )}
              

              

              
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Paper
                    sx={(theme) => ({
                      p: 2,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Thinking...</Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {/* Toggles */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useSemanticSearch}
                      onChange={(e) => setUseSemanticSearch(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AutoAwesomeIcon fontSize="small" />
                      Help chat, semantic
                    </Typography>
                  }
                />
                <Tooltip title="When on, the Help chat may use semantic matching for your questions (not the Ask AI document panel)">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showScreenshots}
                      onChange={(e) => setShowScreenshots(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon fontSize="small" />
                      Visual Guides
                    </Typography>
                  }
                />
                <Tooltip title="Show visual guides with screenshots when available">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Ask a question about how to use Narralyze..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendQuestion()}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleSendQuestion}
                  disabled={!question.trim() || isLoading}
                  startIcon={<SendIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog; 